/**
 * reports/api/reportsApi.js
 *
 * Reports CRUD + run + preview. After wedge 5 ALL execution paths
 * (run/preview/export) live on the server — see backend/modules/
 * analytics_engine. The client just POSTs configs and consumes the
 * RunResult dict that comes back.
 *
 * Endpoints touched here:
 *   GET    /reports                           list (+ filters)
 *   GET    /reports/:id                       detail
 *   POST   /reports                           upsert
 *   DELETE /reports/:id                       delete
 *   POST   /reports/:id/duplicate             server-side copy
 *   POST   /reports/:id/favorite              per-user favorite toggle
 *   POST   /reports/:id/mark-run              stamp lastRun + bump recents
 *   POST   /reports/:id/run                   ← wedge 5 (saved report)
 *   POST   /reports/run                       ← wedge 5 (preview unsaved)
 *
 * Fixture mode (REACT_APP_USE_REPORT_FIXTURES) remains for storybook /
 * disconnected demos. In fixture mode we still need to aggregate in the
 * browser, but that path imports the deprecated formatters module —
 * NOT the engine — so the production bundle stays free of the JS engine.
 */
import { analyticsClient, unwrap } from '../../analytics-shared/api/client';

// Fixture mode short-circuits all CRUD onto an in-memory store so demos
// keep working without a backend.
const USE_FIXTURES = process.env.REACT_APP_USE_REPORT_FIXTURES === 'true';

const uid = (p = 'r') => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const createEmptyFilter = () => ({
  id: uid('grp'), type: 'group', logic: 'AND', children: [],
});

// `reportType` (optional) carries the Salesforce-style relationship metadata
// chosen from the Create Report modal:
//   { id, kind, name, primary: {...}, related: [{ key, via, direction }] | null }
// We persist it on the report config so the future runner can honour
// cross-object joins without rebuilding the relationship from scratch each
// time. Today's runner ignores everything except `object` (the primary key).
export const createReportConfig = (objectKey, reportType = null) => ({
  id: uid('rep'),
  name: reportType?.name || 'Untitled Report',
  description: reportType?.description || '',
  object: objectKey,
  reportType: reportType
    ? {
      id: reportType.id,
      kind: reportType.kind,
      primary: reportType.primary,
      related: reportType.related || null,
    }
    : null,
  folder: 'Unfiled',
  visibility: 'Private',
  tags: [],
  // New reports default to the running user's own records — Salesforce's
  // "Show Me: My records". Users widen to team/all from the filter panel.
  scope: 'my',
  columns: [],
  groupBy: [],
  filters: createEmptyFilter(),
  summaries: {},
  sort: null,
  charts: [],
  schedule: null,
  favorite: false,
  createdBy: 'You',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  lastRun: null,
});

// ─── Fixture-mode in-memory store ──────────────────────────────────────────
// Kept here (not in localStorage) so it never leaks across sessions — fixture
// mode is for storybook / disconnected demos only.
const fixtureStore = { reports: [] };

const fixturesApi = {
  list: async () => fixtureStore.reports,
  get: async (id) => fixtureStore.reports.find((r) => r.id === id) || null,
  save: async (report) => {
    const stamped = { ...report, updatedAt: new Date().toISOString() };
    const idx = fixtureStore.reports.findIndex((r) => r.id === report.id);
    if (idx >= 0) fixtureStore.reports[idx] = stamped;
    else fixtureStore.reports.unshift(stamped);
    return stamped;
  },
  remove: async (id) => {
    fixtureStore.reports = fixtureStore.reports.filter((r) => r.id !== id);
  },
  duplicate: async (id) => {
    const original = fixtureStore.reports.find((r) => r.id === id);
    if (!original) return null;
    const copy = {
      ...JSON.parse(JSON.stringify(original)),
      id: uid('rep'),
      name: `${original.name} (Copy)`,
      favorite: false,
      schedule: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastRun: null,
    };
    fixtureStore.reports.unshift(copy);
    return copy;
  },
  toggleFavorite: async (id) => {
    const r = fixtureStore.reports.find((x) => x.id === id);
    if (r) r.favorite = !r.favorite;
    return r;
  },
  markRun: async (id) => {
    const r = fixtureStore.reports.find((x) => x.id === id);
    if (r) r.lastRun = new Date().toISOString();
    return r;
  },
  // Stubs so the schedule modal doesn't crash in fixture/demo mode.
  // Storybook never exercises the SMTP pipeline; these just round-trip
  // state on the in-memory record.
  saveSchedule: async (id, schedule) => {
    const r = fixtureStore.reports.find((x) => x.id === id);
    if (r) r.schedule = schedule;
    return { id, schedule };
  },
  removeSchedule: async (id) => {
    const r = fixtureStore.reports.find((x) => x.id === id);
    if (r) r.schedule = null;
    return { id, schedule: null };
  },
  pauseSchedule: async (id) => ({ id, paused: true }),
  resumeSchedule: async (id) => ({ id, paused: false }),
  sendNow: async (id) => ({ id, queued: true }),
  listDeliveries: async () => [],
};

// ─── HTTP-mode (production) ────────────────────────────────────────────────

function toParams({ q, folder, visibility, object, favorite } = {}) {
  const params = {};
  if (q) params.q = q;
  if (folder) params.folder = folder;
  if (visibility) params.visibility = visibility;
  // `object` is a reserved word in TS but works as a URL param key.
  if (object) params.object = object;
  if (favorite) params.favorite = true;
  return params;
}

const httpApi = {
  list: async (filters) => analyticsClient
    .get('/reports', { params: toParams(filters) })
    .then(unwrap),

  get: async (id) => analyticsClient
    .get(`/reports/${encodeURIComponent(id)}`)
    .then(unwrap)
    .catch((err) => {
      if (err?.status === 404) return null;
      throw err;
    }),

  // Upsert. Server keys off the client-generated id; on update it verifies
  // ownership and refuses cross-user overwrites within the same tenant.
  save: async (report) => analyticsClient.post('/reports', report).then(unwrap),

  remove: async (id) => analyticsClient.delete(`/reports/${encodeURIComponent(id)}`).then(unwrap),

  duplicate: async (id) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/duplicate`)
    .then(unwrap),

  toggleFavorite: async (id) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/favorite`)
    .then(unwrap),

  markRun: async (id) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/mark-run`)
    .then(unwrap),

  // ─── Scheduling + delivery (wedge 4) ────────────────────────────────────
  // The dedicated PUT/DELETE/POST endpoints compute next_run server-side
  // immediately on save, so a "fire at 14:30, save at 14:28" schedule
  // actually fires at 14:30 instead of waiting up to 60s for the
  // scheduler's backfill tick. They also do real validation (frequency,
  // recipients), unlike the wedge-1 full-report save which accepted the
  // schedule blob verbatim.
  saveSchedule: async (id, schedule) => analyticsClient
    .put(`/reports/${encodeURIComponent(id)}/schedule`, schedule)
    .then(unwrap),

  removeSchedule: async (id) => analyticsClient
    .delete(`/reports/${encodeURIComponent(id)}/schedule`)
    .then(unwrap),

  pauseSchedule: async (id) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/schedule/pause`)
    .then(unwrap),

  resumeSchedule: async (id) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/schedule/resume`)
    .then(unwrap),

  // Fire a delivery immediately. `recipients` optional — when omitted,
  // uses the schedule's saved recipient list. Useful for "Send test
  // now" in the modal so users can verify the full pipeline (export
  // render → SMTP → recipient inbox) without waiting for the schedule.
  sendNow: async (id, recipients) => analyticsClient
    .post(`/reports/${encodeURIComponent(id)}/send-now`, recipients ? { recipients } : {})
    .then(unwrap),

  // Recent delivery history (last N rows from db.report_deliveries).
  // The modal renders these as a small panel — gives users instant
  // visibility into "did my last firing actually deliver?" without
  // having to ask an admin.
  listDeliveries: async (id, limit = 10) => analyticsClient
    .get(`/reports/${encodeURIComponent(id)}/deliveries`, { params: { limit } })
    .then(unwrap),
};

// Cross-tenant scheduler diagnostics. Not per-report, so it lives on
// its own object rather than reportsApi. Returns scheduler liveness +
// pending schedule count + sender config — the modal uses this to
// show a green/red dot and the misconfig banner.
export const schedulerStatusApi = {
  get: async () => analyticsClient.get('/scheduler/status').then(unwrap),
};

// Schedule-attachments — user-uploaded files pinned to a schedule via
// GridFS on the backend. The modal renders an uploader and references
// the returned ids in the schedule's `additional_attachments` array.
// All operations are tenant-scoped server-side.
export const scheduleAttachmentsApi = {
  list: async () => analyticsClient.get('/scheduler/attachments').then(unwrap),
  upload: async (file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return analyticsClient
      .post('/scheduler/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(unwrap);
  },
  remove: async (id) => analyticsClient
    .delete(`/scheduler/attachments/${encodeURIComponent(id)}`)
    .then(unwrap),
};

const backend = USE_FIXTURES ? fixturesApi : httpApi;

export const reportsApi = {
  list: backend.list,
  get: backend.get,
  save: backend.save,
  remove: backend.remove,
  duplicate: backend.duplicate,
  toggleFavorite: backend.toggleFavorite,
  markRun: backend.markRun,
  // Scheduler / subscription helpers — wedge-4 dedicated endpoints.
  // See backend definitions on the `backend` object above.
  saveSchedule: backend.saveSchedule,
  removeSchedule: backend.removeSchedule,
  pauseSchedule: backend.pauseSchedule,
  resumeSchedule: backend.resumeSchedule,
  sendNow: backend.sendNow,
  listDeliveries: backend.listDeliveries,

  /**
   * Run a saved report or preview an unsaved config.
   *
   * After wedge 5 the backend's analytics_engine is the only execution
   * path. We pick the endpoint based on whether the config has a
   * server-side identity yet:
   *
   *   - Saved report (any id that exists in db.reports) →
   *       POST /v1/reports/:id/run (also stamps lastRun on the doc)
   *   - Unsaved draft / preview →
   *       POST /v1/reports/run with the full config in the body
   *
   * Both return the same RunResult shape:
   *   { grouped, tree?, rows?, grandTotals, recordCount, records }
   *
   * Fixture mode delegates to a lazy import so the production bundle
   * doesn't include the fixtures or the deprecated buildReport helper.
   */
  run: async (config /* , overrides */) => {
    if (!config?.object) {
      return { records: [], grouped: false, grandTotals: {}, recordCount: 0 };
    }
    if (USE_FIXTURES) {
      // Lazy import keeps fixture-only code out of the production bundle.
      const { runWithFixtures } = await import('./fixtureRunner');
      return runWithFixtures(config);
    }
    if (config.id) {
      // Saved-report path — server stamps lastRun automatically.
      return analyticsClient
        .post(`/reports/${encodeURIComponent(config.id)}/run`)
        .then(unwrap)
        .catch((err) => {
          // Fall back to the preview endpoint if the report isn't saved
          // server-side yet (404). Lets unsaved-with-an-id flows still
          // render a preview during the very first save.
          if (err?.status === 404) {
            return analyticsClient.post('/reports/run', { config }).then(unwrap);
          }
          throw err;
        });
    }
    return analyticsClient.post('/reports/run', { config }).then(unwrap);
  },

  // Builder live preview — server caps the row scan (sample) and says so
  // via result.meta {preview, truncated, totalPrimaryRecords, rowLimit}.
  preview: async (config) => analyticsClient
    .post('/reports/run', { config, preview: true })
    .then(unwrap),

  // Full (non-preview) run of an arbitrary config — used by the report
  // viewer, incl. drill-down overlays. Large datasets stream server-side
  // so recordCount / grandTotals / chartData are exact even when only
  // the first rows are returned for display (meta.truncated).
  runConfig: async (config) => analyticsClient
    .post('/reports/run', { config, preview: false })
    .then(unwrap),
};

export { uid };
